<template>
    <ui-sheet :value="value" :width="760" @input="$emit('input', $event)">
        <template #header>
            <div v-if="item" class="cp-card__slug">{{ item.protocol || "Sem protocolo" }} · {{ item.person_type_label }}</div>
            <h3 class="ui-section-title">{{ item ? item.name : "Carregando..." }}</h3>
            <div v-if="item" class="d-flex align-center flex-wrap mt-1" style="gap: 6px;">
                <ui-badge set="cp-registration" :status="item.status" />
                <ui-badge v-if="item.nda_accepted_at" :text="`NDA v${item.nda_version} assinado`" tone="primary" :dot="false" />
                <v-spacer></v-spacer>
                <v-btn v-if="item.status !== 'draft'" x-small outlined :loading="reporting" title="Gera a versão dos registros atuais (ou abre a mesma, se nada mudou)" @click="evidenceReport"><v-icon left x-small>mdi-file-certificate-outline</v-icon>Relatório de evidências</v-btn>
            </div>
        </template>

        <div v-if="loading && !item" class="ui-empty"><v-progress-circular indeterminate size="28" color="primary"></v-progress-circular></div>
        <template v-else-if="item">
            <div v-if="item.status === 'correction' && item.correction_note" class="cp-notice mt-4">
                <v-icon small>mdi-message-alert-outline</v-icon>
                <span><strong>Correção pedida:</strong> {{ item.correction_note }}</span>
            </div>
            <div v-if="item.decision_reason && ['approved', 'rejected'].includes(item.status)" class="cp-notice mt-4">
                <v-icon small>mdi-gavel</v-icon>
                <span><strong>{{ item.status === "rejected" ? "Motivo da inabilitação:" : "Observação:" }}</strong> {{ item.decision_reason }}</span>
            </div>

            <div class="ui-label mt-4">Proponente</div>
            <dl class="ui-kv">
                <dt>{{ item.person_type === "pj" ? "CNPJ" : "CPF" }}</dt><dd class="ui-mono">{{ item.document_formatted }}</dd>
                <template v-if="item.person_type === 'pj'">
                    <dt>Representante</dt><dd>{{ item.representative_name || "—" }}<span v-if="item.representative_cpf" class="ui-muted"> · CPF {{ formatCpf(item.representative_cpf) }}</span></dd>
                </template>
                <dt>E-mail</dt><dd>{{ item.email || "—" }}</dd>
                <dt>Celular</dt><dd>{{ item.phone || "—" }}</dd>
                <dt>Enviada em</dt><dd>{{ formatStamp(item.submitted_at) }}</dd>
                <dt>Decisão em</dt><dd>{{ formatStamp(item.decided_at) }}</dd>
                <template v-if="item.deposit">
                    <dt>Caução</dt><dd>{{ formatMoney(item.deposit.amount) }}</dd>
                </template>
            </dl>

            <div class="ui-label mt-5 mb-2">Assinaturas ICP-Brasil</div>
            <ul class="cp-docs">
                <li v-for="row in signatureRows" :key="row.kind" class="cp-doc">
                    <div class="cp-doc__text">
                        <div class="ui-cell-main">{{ row.title }}</div>
                        <template v-if="row.signature">
                            <div class="ui-cell-sub">{{ row.signature.signed_as_label }} · {{ row.signature.signer_name }} · {{ signerDocument(row.signature) }}</div>
                            <div class="ui-cell-sub">Assinado em {{ formatStamp(row.signature.signed_at, { seconds: true }) }}<span v-if="row.signature.ip"> · IP {{ row.signature.ip }}</span></div>
                            <div class="ui-cell-sub">Certificado {{ row.signature.certificate_serial }} · {{ issuerName(row.signature.certificate_issuer) }}</div>
                            <div class="ui-cell-sub ui-mono" :title="row.signature.file_hash">SHA-256 {{ row.signature.file_hash.slice(0, 16) }}…</div>
                        </template>
                        <div v-else class="ui-cell-sub cp-warn">{{ row.missing }}</div>
                        <div v-if="row.outdated" class="ui-cell-sub cp-warn">{{ row.outdated }}</div>
                    </div>
                    <ui-badge :text="row.signature && !row.outdated ? 'Assinado' : 'Pendente'" :tone="row.signature && !row.outdated ? 'success' : 'warning'" />
                    <v-btn v-if="row.signature" icon small title="Abrir PDF assinado" @click="openSignature(row.signature)"><v-icon small>mdi-open-in-new</v-icon></v-btn>
                </li>
            </ul>

            <div class="d-flex align-center mt-5 mb-2">
                <div class="ui-label mb-0">Documentos exigidos</div>
                <v-spacer></v-spacer>
                <span class="ui-small ui-muted">{{ approvedCount }} de {{ item.checklist.length }} aprovados</span>
            </div>
            <ul class="cp-docs">
                <li v-for="doc in item.checklist" :key="`r${doc.id}`" class="cp-doc">
                    <div class="cp-doc__text">
                        <div class="ui-cell-main">{{ doc.title }}<span v-if="!doc.required" class="ui-muted"> (opcional)</span></div>
                        <div v-if="doc.file" class="ui-cell-sub">{{ doc.file.file_name }} · {{ formatBytes(doc.file.file_size) }} · enviado em {{ formatStamp(doc.file.uploaded_at) }}</div>
                        <div v-else class="ui-cell-sub cp-warn">Não enviado</div>
                        <div v-if="doc.file && doc.file.review_note" class="ui-cell-sub">Motivo: {{ doc.file.review_note }}</div>
                    </div>
                    <template v-if="doc.file">
                        <ui-badge set="cp-registration-doc" :status="doc.file.status" />
                        <document-actions :file="doc.file" :can-review="canReview" @open="openFile" @review="review" />
                    </template>
                </li>
            </ul>

            <template v-if="item.extra_documents.length">
                <div class="ui-label mt-5 mb-2">Documentos adicionais</div>
                <ul class="cp-docs">
                    <li v-for="file in item.extra_documents" :key="`x${file.id}`" class="cp-doc">
                        <div class="cp-doc__text">
                            <div class="ui-cell-main">{{ file.title }}</div>
                            <div class="ui-cell-sub">{{ file.file_name }} · {{ formatBytes(file.file_size) }}</div>
                            <div v-if="file.review_note" class="ui-cell-sub">Motivo: {{ file.review_note }}</div>
                        </div>
                        <ui-badge set="cp-registration-doc" :status="file.status" />
                        <document-actions :file="file" :can-review="canReview" @open="openFile" @review="review" />
                    </li>
                </ul>
            </template>

            <p v-if="item.status === 'submitted' && item.approval_blockers.length" class="ui-small ui-muted mt-4 mb-0">
                Para habilitar, aprove: {{ item.approval_blockers.join(", ") }}.
            </p>
        </template>

        <template #footer>
            <template v-if="item && $allowed('competitive.judge')">
                <v-btn v-if="can('reject')" text class="ui-btn-danger" :disabled="busy" @click="askText('reject')">Inabilitar</v-btn>
                <v-btn v-if="can('reopen')" text :disabled="busy" @click="decide('reopen')">Reabrir análise</v-btn>
                <v-spacer></v-spacer>
                <v-btn v-if="can('request_correction')" outlined :disabled="busy" @click="askText('request_correction')">Pedir correção</v-btn>
                <v-btn v-if="can('approve')" color="primary" depressed :loading="busy" :disabled="item.approval_blockers.length > 0" @click="confirmApprove">Habilitar</v-btn>
            </template>
            <template v-else>
                <v-spacer></v-spacer>
                <v-btn outlined @click="$emit('input', false)">Fechar</v-btn>
            </template>
        </template>

        <v-dialog v-model="ask.open" max-width="520" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">{{ ask.title }}</h3>
                        <p class="ui-section-description">{{ ask.description }}</p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <v-textarea v-model="ask.text" :label="ask.label" outlined dense rows="4" auto-grow hide-details="auto" autofocus></v-textarea>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined :disabled="busy" @click="ask.open = false">Cancelar</v-btn>
                    <v-btn :color="ask.danger ? 'error' : 'primary'" depressed :loading="busy" :disabled="!ask.text.trim()" @click="confirmAsk">{{ ask.confirm }}</v-btn>
                </div>
            </v-card>
        </v-dialog>
    </ui-sheet>
</template>

<script>
import axios from "axios";
import { API, formatBytes, formatMoney, formatStamp, notifyError, notifySuccess, openUrl } from "@/components/competitive/competitive.util";

// Abrir, aprovar e recusar um arquivo da inscricao.
const DocumentActions = {
    name: "DocumentActions",
    props: { file: { type: Object, required: true }, canReview: { type: Boolean, default: false } },
    render(h) {
        const actions = [{ text: "Abrir", icon: "mdi-open-in-new", handler: () => this.$emit("open", this.file) }];
        if (this.canReview) {
            actions.push({ text: "Aprovar", icon: "mdi-check", handler: () => this.$emit("review", this.file, "approved"), disabled: this.file.status === "approved" });
            actions.push({ text: "Recusar", icon: "mdi-close", handler: () => this.$emit("review", this.file, "rejected"), danger: true, disabled: this.file.status === "rejected" });
            if (this.file.status !== "pending") actions.push({ text: "Voltar para conferência", icon: "mdi-undo", handler: () => this.$emit("review", this.file, "pending") });
        }
        return h("ui-row-actions", { props: { actions } });
    },
};

const TRANSITIONS = {
    request_correction: ["submitted"],
    approve: ["submitted"],
    reject: ["submitted", "correction", "approved"],
    reopen: ["rejected"],
};

export default {
    name: "CompetitiveRegistrationSheet",
    components: { DocumentActions },
    props: {
        value: { type: Boolean, default: false },
        registrationId: { type: Number, default: null },
    },
    data: () => ({
        item: null,
        loading: false,
        busy: false,
        reporting: false,
        ask: { open: false, action: null, title: "", description: "", label: "", confirm: "", danger: false, text: "", file: null },
    }),
    computed: {
        canReview() {
            return !!this.item && this.item.status === "submitted" && this.$allowed("competitive.judge");
        },
        // Ultima assinatura de cada termo; o NDA so aparece depois da habilitacao.
        signatureRows() {
            if (!this.item) return [];
            const latest = (kind) => (this.item.signatures || []).find((sig) => sig.kind === kind) || null;
            const term = latest("term");
            const rows = [{
                kind: "term",
                title: `Termo de inscrição${term ? ` v${term.document_version}` : ""}`,
                signature: term,
                missing: "Ainda não assinado. O proponente assina antes de enviar a inscrição.",
                // Depois de enviada, a inscricao vale com a versao assinada.
                outdated: term && !this.item.term.signed && ["draft", "correction"].includes(this.item.status) ? `O termo vigente é a versão ${this.item.term.version}. O proponente assina de novo antes de enviar.` : null,
            }];
            const ndaVersion = this.item.process ? this.item.process.nda_version : 0;
            const nda = latest("nda");
            if (nda || this.item.status === "approved") {
                rows.push({
                    kind: "nda",
                    title: `Termo de confidencialidade${nda ? ` v${nda.document_version}` : ""}`,
                    signature: nda,
                    missing: ndaVersion ? "Ainda não assinado. Sem ele, o habilitado não acessa o data room." : "O termo de confidencialidade ainda não foi publicado.",
                    outdated: nda && ndaVersion && nda.document_version !== ndaVersion ? `O termo vigente é a versão ${ndaVersion}. O data room fica bloqueado até nova assinatura.` : null,
                });
            }
            return rows;
        },
        approvedCount() {
            return this.item ? this.item.checklist.filter((doc) => doc.file && doc.file.status === "approved").length : 0;
        },
    },
    watch: {
        value(open) {
            if (open) this.load();
        },
        registrationId() {
            if (this.value) this.load();
        },
    },
    methods: {
        formatBytes,
        formatMoney,
        formatStamp,
        formatCpf(value) {
            return String(value || "").replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
        },
        signerDocument(signature) {
            if (signature.signer_cnpj) return `CNPJ ${String(signature.signer_cnpj).replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5")}${signature.signer_cpf ? ` · responsável CPF ${this.formatCpf(signature.signer_cpf)}` : ""}`;
            return `CPF ${this.formatCpf(signature.signer_cpf)}`;
        },
        issuerName(issuer) {
            const match = String(issuer || "").match(/CN=([^,]+)/);
            return match ? match[1] : issuer;
        },
        // Relatorio tecnico de evidencias da inscricao (D9): mesma base, mesmo arquivo.
        async evidenceReport() {
            this.reporting = true;
            try {
                const report = (await axios.post(`${API}/registrations/${this.registrationId}/evidence-report`)).data.content;
                const file = await axios.get(`${API}/evidence-reports/${report.id}/file`);
                openUrl(file.data.content.url);
                notifySuccess(this, `Relatório ${report.public_id}, versão ${report.version}.`);
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.reporting = false;
            }
        },
        async openSignature(signature) {
            try {
                const result = await axios.get(`${API}/signatures/${signature.id}/file`);
                openUrl(result.data.content.url);
            } catch (error) {
                notifyError(this, error);
            }
        },
        can(action) {
            return !!this.item && TRANSITIONS[action].includes(this.item.status);
        },
        async load() {
            if (!this.registrationId) return;
            this.loading = true;
            this.item = null;
            try {
                const result = await axios.get(`${API}/registrations/${this.registrationId}`);
                this.item = result.data.content;
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.loading = false;
            }
        },
        async openFile(file) {
            try {
                const result = await axios.get(`${API}/registration-documents/${file.id}/file`);
                openUrl(result.data.content.url);
            } catch (error) {
                notifyError(this, error);
            }
        },
        review(file, status) {
            if (status === "rejected") {
                this.ask = { open: true, action: "review", title: "Recusar documento", description: `${file.title}: o proponente vê o motivo quando a correção for pedida.`, label: "Motivo da recusa", confirm: "Recusar", danger: true, text: "", file };
                return;
            }
            this.sendReview(file, status, "");
        },
        async sendReview(file, status, note) {
            this.busy = true;
            try {
                const result = await axios.post(`${API}/registration-documents/${file.id}/review`, { status, note });
                this.item = result.data.content;
                this.ask.open = false;
                this.$emit("changed");
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.busy = false;
            }
        },
        askText(action) {
            const texts = {
                request_correction: { title: "Pedir correção", description: "A inscrição volta para o proponente, que recebe este texto por e-mail e reenvia os documentos.", label: "O que corrigir", confirm: "Pedir correção", danger: false },
                reject: { title: "Inabilitar proponente", description: "O proponente recebe o motivo por e-mail. A decisão fica na trilha do certame.", label: "Motivo da inabilitação", confirm: "Inabilitar", danger: true },
            };
            this.ask = { open: true, action, text: "", file: null, ...texts[action] };
        },
        confirmAsk() {
            if (this.ask.action === "review") this.sendReview(this.ask.file, "rejected", this.ask.text.trim());
            else this.decide(this.ask.action, this.ask.text.trim());
        },
        async confirmApprove() {
            const confirmed = await this.$dialog.confirm({
                title: "Habilitar proponente",
                text: `${this.item.name} passa a habilitado e recebe um e-mail. O acesso ao data room depende da assinatura do termo de confidencialidade. Confirma?`,
                actions: { false: "Cancelar", true: { text: "Habilitar", color: "primary" } },
            });
            if (confirmed) this.decide("approve");
        },
        async decide(action, text = "") {
            const paths = { request_correction: "request-correction", approve: "approve", reject: "reject", reopen: "reopen" };
            this.busy = true;
            try {
                const body = action === "request_correction" ? { note: text } : { reason: text };
                const result = await axios.post(`${API}/registrations/${this.item.id}/${paths[action]}`, body);
                this.item = result.data.content;
                this.ask.open = false;
                notifySuccess(this, "Inscrição atualizada.");
                this.$emit("changed");
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.busy = false;
            }
        },
    },
};
</script>
